// Page de visualisation des conversations de l'agent IA
// Liste des conversations + vue détaillée avec possibilité de reprendre la main

import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  MessageSquare, User, Bot, ChevronLeft, Send, 
  Clock, CheckCircle, AlertCircle, HandMetal,
  Play, Pause, Phone, Mail, FileText, Star
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Card, CardContent } from '../../../components/ui/card';
import { toast } from 'sonner';

import { API_URL } from '../../../config/api';

function formatTime(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);
  
  if (diff < 60) return 'À l\'instant';
  if (diff < 3600) return `Il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `Il y a ${Math.floor(diff / 3600)}h`;
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

// Conversation List Item
function ConversationListItem({ conversation, isActive, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-start gap-3 p-4 text-left hover:bg-gray-50 transition-colors border-b ${
        isActive ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
      }`}
    >
      <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
        <User size={20} className="text-gray-500" />
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <p className="font-medium text-gray-900 truncate">
            {conversation.visitor_name || 'Visiteur anonyme'}
          </p>
          <span className="text-xs text-gray-500">
            {formatTime(conversation.last_message_at || conversation.created_at)}
          </span>
        </div>
        
        {conversation.main_request && (
          <p className="text-sm text-gray-600 truncate mt-0.5">
            {conversation.main_request}
          </p>
        )}
        
        <div className="flex items-center gap-2 mt-1">
          {conversation.is_human_takeover && (
            <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full">
              En cours
            </span>
          )}
          <span className={`text-xs px-2 py-0.5 rounded-full ${
            conversation.lead_status === 'qualified' ? 'bg-green-100 text-green-700' :
            conversation.lead_status === 'converted' ? 'bg-purple-100 text-purple-700' :
            'bg-gray-100 text-gray-600'
          }`}>
            {conversation.lead_status === 'qualified' ? 'Qualifié' :
             conversation.lead_status === 'converted' ? 'Converti' :
             conversation.lead_status === 'new' ? 'Nouveau' : conversation.lead_status}
          </span>
          <span className="text-xs text-gray-500">
            {conversation.message_count || 0} msg
          </span>
        </div>
      </div>
    </button>
  );
}

// Message Bubble
function MessageBubble({ message }) {
  const isUser = message.role === 'user';
  const isSystem = message.role === 'system';
  
  if (isSystem) {
    return (
      <div className="text-center py-2">
        <span className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
          {message.content}
        </span>
      </div>
    );
  }
  
  return (
    <div className={`flex gap-2 ${isUser ? 'justify-end' : 'justify-start'}`}>
      {!isUser && (
        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
          <Bot size={16} className="text-blue-600" />
        </div>
      )}
      
      <div className={`max-w-[70%] rounded-2xl px-4 py-2 ${
        isUser 
          ? 'bg-gray-100 text-gray-900 rounded-br-md' 
          : 'bg-blue-600 text-white rounded-bl-md'
      }`}>
        <p className="whitespace-pre-wrap">{message.content}</p>
        <div className={`flex items-center justify-end gap-1 mt-1 text-xs ${
          isUser ? 'text-gray-400' : 'text-blue-200'
        }`}>
          <span>{new Date(message.created_at).toLocaleTimeString('fr-FR', { 
            hour: '2-digit', 
            minute: '2-digit' 
          })}</span>
          {message.is_ai_generated && !isUser && (
            <Bot size={12} />
          )}
        </div>
      </div>
      
      {isUser && (
        <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
          <User size={16} className="text-gray-500" />
        </div>
      )}
    </div>
  );
}

export default function ConversationsPage() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  
  const [conversations, setConversations] = useState([]);
  const [selectedConvo, setSelectedConvo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [filter, setFilter] = useState('all');

  const token = localStorage.getItem('clientToken');

  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    if (conversationId) {
      fetchConversation(conversationId);
    }
  }, [conversationId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async () => {
    try {
      const res = await fetch(`${API_URL}/api/vendor/conversations?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setConversations(await res.json());
      }
    } catch (e) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const fetchConversation = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/vendor/conversations/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedConvo(data.conversation);
        setMessages(data.messages);
      }
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const handleTakeover = async () => {
    try {
      const res = await fetch(`${API_URL}/api/vendor/conversations/${conversationId}/takeover`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        setSelectedConvo({ ...selectedConvo, is_human_takeover: true });
        toast.success('Vous avez repris la conversation');
      }
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const handleRelease = async () => {
    try {
      const res = await fetch(`${API_URL}/api/vendor/conversations/${conversationId}/release`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        setSelectedConvo({ ...selectedConvo, is_human_takeover: false });
        toast.success('L\'agent IA reprend la conversation');
      }
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConvo?.is_human_takeover) return;
    
    setSending(true);
    
    // TODO: Implement manual message sending endpoint
    // For now, just add locally
    setMessages([...messages, {
      id: Date.now().toString(),
      conversation_id: conversationId,
      role: 'assistant',
      content: newMessage.trim(),
      is_ai_generated: false,
      created_at: new Date().toISOString()
    }]);
    setNewMessage('');
    setSending(false);
    
    toast.info('Message envoyé (note: nécessite implémentation WebSocket)');
  };

  const filteredConversations = conversations.filter(c => {
    if (filter === 'active') return c.is_active;
    if (filter === 'takeover') return c.is_human_takeover;
    if (filter === 'qualified') return c.lead_status === 'qualified';
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-12rem)] flex bg-white rounded-xl shadow-sm overflow-hidden" data-testid="conversations-page">
      {/* Conversations List */}
      <div className={`w-full md:w-96 border-r border-gray-200 flex flex-col ${
        conversationId ? 'hidden md:flex' : 'flex'
      }`}>
        <div className="p-4 border-b">
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <MessageSquare size={24} />
            Conversations
          </h1>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="all">Toutes ({conversations.length})</option>
            <option value="active">Actives</option>
            <option value="takeover">En cours de traitement</option>
            <option value="qualified">Leads qualifiés</option>
          </select>
        </div>
        
        <div className="flex-1 overflow-y-auto">
          {filteredConversations.length === 0 ? (
            <div className="text-center py-12 px-4">
              <MessageSquare size={32} className="mx-auto text-gray-300 mb-2" />
              <p className="text-gray-500">Aucune conversation</p>
            </div>
          ) : (
            filteredConversations.map((conv) => (
              <ConversationListItem
                key={conv.id}
                conversation={conv}
                isActive={conv.id === conversationId}
                onClick={() => navigate(`/espace-client/ai-vendor/conversations/${conv.id}`)}
              />
            ))
          )}
        </div>
      </div>

      {/* Conversation Detail */}
      <div className={`flex-1 flex flex-col ${
        conversationId ? 'flex' : 'hidden md:flex'
      }`}>
        {selectedConvo ? (
          <>
            {/* Header */}
            <div className="p-4 border-b flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => navigate('/espace-client/ai-vendor/conversations')}
                  className="md:hidden p-2 -ml-2"
                >
                  <ChevronLeft size={24} />
                </button>
                
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                  <User size={20} className="text-gray-500" />
                </div>
                
                <div>
                  <h2 className="font-semibold text-gray-900">
                    {selectedConvo.visitor_name || 'Visiteur anonyme'}
                  </h2>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    {selectedConvo.visitor_email && (
                      <span className="flex items-center gap-1">
                        <Mail size={12} />
                        {selectedConvo.visitor_email}
                      </span>
                    )}
                    {selectedConvo.visitor_phone && (
                      <span className="flex items-center gap-1">
                        <Phone size={12} />
                        {selectedConvo.visitor_phone}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                {selectedConvo.is_human_takeover ? (
                  <Button variant="outline" onClick={handleRelease} size="sm">
                    <Play size={16} className="mr-1" />
                    Remettre à l'IA
                  </Button>
                ) : (
                  <Button onClick={handleTakeover} size="sm" className="bg-orange-500 hover:bg-orange-600">
                    <HandMetal size={16} className="mr-1" />
                    Reprendre la main
                  </Button>
                )}
              </div>
            </div>
            
            {/* Info Banner */}
            {selectedConvo.is_human_takeover && (
              <div className="bg-orange-50 border-b border-orange-200 px-4 py-2 text-sm text-orange-800 flex items-center gap-2">
                <HandMetal size={16} />
                Vous avez repris cette conversation. L'IA ne répondra plus automatiquement.
              </div>
            )}
            
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((msg) => (
                <MessageBubble key={msg.id} message={msg} />
              ))}
              <div ref={messagesEndRef} />
            </div>
            
            {/* Input */}
            {selectedConvo.is_human_takeover && (
              <form onSubmit={sendMessage} className="p-4 border-t">
                <div className="flex items-center gap-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Écrire une réponse..."
                    className="flex-1"
                  />
                  <Button
                    type="submit"
                    disabled={!newMessage.trim() || sending}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Send size={18} />
                  </Button>
                </div>
              </form>
            )}
            
            {/* Lead Info Sidebar (visible on larger screens) */}
            <div className="hidden lg:block w-72 border-l p-4 space-y-4">
              <h3 className="font-semibold text-gray-900">Informations Lead</h3>
              
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-gray-500 uppercase">Statut</p>
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm ${
                    selectedConvo.lead_status === 'qualified' ? 'bg-green-100 text-green-700' :
                    selectedConvo.lead_status === 'converted' ? 'bg-purple-100 text-purple-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {selectedConvo.lead_status === 'qualified' && <CheckCircle size={14} />}
                    {selectedConvo.lead_status === 'qualified' ? 'Qualifié' :
                     selectedConvo.lead_status === 'converted' ? 'Converti' :
                     selectedConvo.lead_status === 'new' ? 'Nouveau' : selectedConvo.lead_status}
                  </span>
                </div>
                
                {selectedConvo.lead_score && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Score</p>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-green-500 h-2 rounded-full"
                          style={{ width: `${selectedConvo.lead_score}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium">{selectedConvo.lead_score}%</span>
                    </div>
                  </div>
                )}
                
                {selectedConvo.main_request && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Demande</p>
                    <p className="text-sm text-gray-900">{selectedConvo.main_request}</p>
                  </div>
                )}
                
                <div>
                  <p className="text-xs text-gray-500 uppercase">Créé le</p>
                  <p className="text-sm text-gray-900">
                    {new Date(selectedConvo.created_at).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <MessageSquare size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">Sélectionnez une conversation</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
